import { useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { standardSchemaResolver } from '@hookform/resolvers/standard-schema'
import { AlertTriangle, Info, Loader2, Pencil, RotateCw } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ApiError, CODE } from '@/lib/api'
import { localStamp } from '@/lib/datetime'
import {
  PLATFORMS,
  extractPlaceholders,
  messageSchema,
  messageSchemaFor,
  placeholderProblem,
  translationSyntaxProblem,
  useCreateMessage,
  useModules,
  useOverrideTranslation,
  useRetranslate,
  useSaveTranslation,
  useUpdateMessage,
  failureText,
  type FailureReason,
  type MessageDetail,
  type MessageInput,
  type TranslationState,
} from '@/features/i18n/api'
import { StatusBadge } from '@/features/i18n/status-badge'
import { ConfirmAction } from '@/components/confirm-action'

/**
 * The English box and every translation for one key, in one dialog.
 *
 * Two things here are not cosmetic:
 *
 *  - Saving sends **only the fields the operator changed** (D-11). The backend
 *    treats an absent field as "do not touch", and an English change clears
 *    every machine translation under the key — so a form that posted itself
 *    wholesale would wipe the catalog one context-note typo at a time.
 *
 *  - Placeholder validation runs on every keystroke and gates Save (D-9),
 *    against human edits exactly as against machine output. A translator has a
 *    bare textarea and no system prompt, and because saving marks the row
 *    `is_manual` it becomes exempt from every future automated refresh — so a
 *    placeholder broken here is broken permanently.
 */

interface MessageEditorProps {
  /** Absent for a new key. Loaded by the parent so the form can seed from it. */
  detail?: MessageDetail
  /**
   * Re-reads the key and resolves with what came back. Owned by the parent
   * because the parent owns the query; used to collect a machine draft after a
   * retranslate, which the backend runs in the background.
   */
  refetchDetail?: () => Promise<MessageDetail | undefined>
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * `display_name` is the endonym — 日本語, 한국어, 繁體中文 — because it is what
 * the consumer app renders in its language picker. That is right for users and
 * awkward for staff, who are scanning a dozen rows for the one they mean.
 *
 * So the English name is shown above it, derived at render rather than stored.
 * `llm_name` is the other English name we hold, but it belongs to the prompt:
 * an operator tuning it to "Traditional Chinese, formal register" for better
 * model output would be silently renaming a column heading.
 */
const ENGLISH_NAMES = (() => {
  try {
    return new Intl.DisplayNames(['en'], { type: 'language' })
  } catch {
    return null
  }
})()

function englishName(code: string, fallback: string): string {
  try {
    const name = ENGLISH_NAMES?.of(code)
    // Intl echoes the input back for a tag it does not know.
    if (name && name.toLowerCase() !== code.toLowerCase()) return name
  } catch {
    // Structurally invalid tag — a code someone typed by hand. Fall through.
  }
  return fallback
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

/**
 * How long to keep looking for a machine draft after asking for one. The write
 * happens in a background goroutine, so there is nothing to read the moment the
 * request returns — a single refetch would reliably show the cleared row and
 * read as "Retranslate wiped my translation".
 *
 * THE TOTAL HAS TO OUTLAST ONE PROVIDER CALL, and it did not. These delays used
 * to sum to ~11s while `i18n.translation.timeout_secs` defaults to 45 — so
 * against a real model (rather than the instant mock) the loop gave up while the
 * call was still legitimately in flight and told the operator the draft "has not
 * come back yet". The draft then landed seconds later, which trains people to
 * re-click a control that was working. Now ~50s: the 45s ceiling plus a margin
 * for the semaphore queue in front of it.
 *
 * Later delays are long because the interesting window is the end, not the
 * start: a mock answers on the first tick, and a real call answers near its
 * timeout. Polling every second in between would just be 40 wasted requests.
 */
const POLL_DELAYS_MS = [800, 1200, 2000, 3000, 4000, 6000, 8000, 8000, 8000, 8000]

/** Shared by the header and the rows so the columns actually line up. */
const ROW_GRID = 'grid grid-cols-[minmax(140px,190px)_120px_1fr_auto] gap-x-4'

interface Draft {
  content: string
  /** What the server currently holds, so Save can skip untouched rows. */
  original: string
  is_manual: boolean
  needs_review: boolean
  status: TranslationState
  display_name: string
  is_rtl: boolean
  override_reason: string | null
  failure_reason: FailureReason | null
  failed_at: string | null
}

/**
 * The badge for a row as it stands in the editor, including unsaved edits.
 *
 * This is deliberately NOT `deriveStatus` from the server: it has to describe a
 * row mid-edit, which the server never sees. But it follows the same precedence,
 * and that is the part that was wrong — `needs_review` outranks everything, and
 * an inline version of this rule omitted it, so a row being overridden showed
 * "Human" right up to the save that would put it straight into the review queue.
 * `pendingOverride` is what the caller knows and the draft does not: the reason
 * box is open, so this save is going to land as needs_review.
 */
function previewStatus(draft: Draft, pendingOverride: boolean): TranslationState {
  // Empty and untouched: the server's answer, because only it can tell `failed`
  // from `missing` — returning `missing` here first painted a failed row "Ships
  // EN" and hid the "Translation failed: …" line below it. Emptied by an edit:
  // this save would leave nothing, which ships the English like any missing row.
  if (!draft.content.trim()) return draft.content === draft.original ? draft.status : 'missing'
  // An override is the one save that ENTERS the review queue, so it outranks the
  // edit below — otherwise the row reads "Human" until the moment it saves and
  // reappears as needing review, which looks like a bug rather than the point.
  if (pendingOverride) return 'needs_review'
  // Edited but not yet saved: this save sets is_manual and CLEARS needs_review,
  // so it becomes human even if the server currently flags it. Showing the old
  // badge under text somebody just typed reads as though the edit did not take.
  if (draft.content !== draft.original) return 'human'
  // Untouched — the server's own answer, which already ranks needs_review above
  // human above machine. No second copy of that precedence here.
  return draft.status
}

function toDrafts(detail?: MessageDetail): Record<string, Draft> {
  const drafts: Record<string, Draft> = {}
  for (const entry of detail?.translations ?? []) {
    drafts[entry.language_code] = {
      content: entry.content ?? '',
      original: entry.content ?? '',
      is_manual: entry.is_manual,
      needs_review: entry.needs_review,
      status: entry.status,
      display_name: entry.display_name,
      is_rtl: entry.is_rtl,
      override_reason: entry.override_reason,
      failure_reason: entry.failure_reason,
      failed_at: entry.failed_at,
    }
  }
  return drafts
}

export function MessageEditor({
  detail,
  refetchDetail,
  open,
  onOpenChange,
}: MessageEditorProps) {
  const isNew = detail === undefined
  const modules = useModules()
  const createMessage = useCreateMessage()
  const updateMessage = useUpdateMessage()
  const saveTranslation = useSaveTranslation()
  const overrideTranslation = useOverrideTranslation()
  const retranslate = useRetranslate()

  const form = useForm<MessageInput>({
    // The form is built after `detail` loads (see CLAUDE.md), so the schema can
    // close over the row it is editing: the placeholder-form rule only fires on
    // English that changes or a key moving onto the web.
    resolver: standardSchemaResolver(messageSchemaFor(detail)),
    defaultValues: {
      message_key: detail?.message_key ?? '',
      module: detail?.module ?? 'common',
      platform: detail?.platform ?? 'all',
      // A null baseline means no English row has ever existed for this key —
      // real, and only reachable via import. The textarea cannot express null,
      // so editing one necessarily writes a string, which is the correct fix.
      baseline_content: detail?.baseline_content ?? '',
      context_desc: detail?.context_desc ?? '',
    },
  })

  const [drafts, setDrafts] = useState<Record<string, Draft>>(() => toDrafts(detail))
  const [overriding, setOverriding] = useState<string | null>(null)
  const [reason, setReason] = useState('')
  const [saving, setSaving] = useState(false)
  /** Language codes whose machine draft is being regenerated right now. */
  const [regenerating, setRegenerating] = useState<Record<string, boolean>>({})
  /** Language code whose human translation is pending a replace confirmation. */
  const [replacing, setReplacing] = useState<string | null>(null)
  /**
   * The key field is read-only until this is on. Renaming is a different act
   * from editing copy — it is the one change in this dialog that can break the
   * running app, because frontend call sites reference the string verbatim and
   * nothing here can see them. So it is deliberate, separately saved, and
   * confirmed.
   */
  const [renaming, setRenaming] = useState(false)
  const [renameConfirm, setRenameConfirm] = useState(false)
  const [savingKey, setSavingKey] = useState(false)

  /**
   * Adopt server state whenever `detail` refetches.
   *
   * `drafts` is seeded once by `useState`, so without this a save that cascades
   * leaves every machine row showing what it held *before* the cascade — and
   * because a baseline change clears those rows first, that is blank. The dialog
   * then reads as though every machine translation was destroyed. Closing and
   * reopening does not help: this component stays mounted, so the initialiser
   * never runs again and only a full page load recovers.
   *
   * Merged, not overwritten. A row the operator has typed into
   * (`content !== original`) keeps their text and takes only the new `original`,
   * so Save still knows what the server holds and still skips untouched rows.
   */
  useEffect(() => {
    if (!detail) return
    setDrafts((current) => {
      const next = toDrafts(detail)
      for (const [code, draft] of Object.entries(next)) {
        const mine = current[code]
        if (mine && mine.content !== mine.original) draft.content = mine.content
      }
      return next
    })
  }, [detail])

  // The poll below outlives a close, so it must not write state afterwards.
  const alive = useRef(true)
  useEffect(() => {
    alive.current = true
    return () => {
      alive.current = false
    }
  }, [])

  const baseline = form.watch('baseline_content')
  const originalBaseline = detail?.baseline_content ?? ''
  const baselineChanged = !isNew && baseline !== originalBaseline
  const required = useMemo(() => extractPlaceholders(baseline), [baseline])

  const platform = form.watch('platform')

  /**
   * Per-row problems. `overridable` is false when the placeholder FORM is wrong:
   * the override endpoint lifts the mismatch check only, so offering it there
   * would send the operator into a second refusal.
   *
   * `blocking` is whether it stops the Save. A form problem stops it only on a
   * row this save sends (one edited here): the server checks the form on the
   * rows it is sent and nothing else. A machine draft can carry one — the
   * translator checks only that its placeholders match the English (D-9), so a
   * stray brace passes — and it used to lock every other edit in the dialog
   * until a person rewrote that row, which made it a human translation the
   * machine never updates again. It is still shown on the row.
   */
  const problems = useMemo(() => {
    const found: Record<string, { message: string; overridable: boolean; blocking: boolean } | null> =
      {}
    for (const [code, draft] of Object.entries(drafts)) {
      const mismatch = placeholderProblem(baseline, draft.content)
      const syntax = translationSyntaxProblem(platform, baseline, draft.content)
      found[code] =
        mismatch || syntax
          ? {
              message: [mismatch?.message, syntax].filter(Boolean).join(' '),
              overridable: !syntax,
              blocking: Boolean(mismatch) || draft.content !== draft.original,
            }
          : null
    }
    return found
  }, [drafts, baseline, platform])

  const blockedCount = Object.values(problems).filter((problem) => problem?.blocking).length

  /**
   * Warns before the operator commits, because the cascade is invisible
   * otherwise. Counts only rows they have not themselves edited in this dialog —
   * a row they just retyped is about to be saved as their text regardless.
   */
  const cascade = useMemo(() => {
    if (!baselineChanged) return null
    let machine = 0
    let human = 0
    for (const draft of Object.values(drafts)) {
      if (draft.content !== draft.original || !draft.original.trim()) continue
      if (draft.is_manual) human++
      else machine++
    }
    return machine || human ? { machine, human } : null
  }, [baselineChanged, drafts])

  function setContent(code: string, value: string) {
    setDrafts((current) => ({ ...current, [code]: { ...current[code], content: value } }))
  }

  async function onSubmit(values: MessageInput) {
    setSaving(true)
    try {
      if (isNew) {
        await createMessage.mutateAsync(values)
        toast.success('Key created. Machine drafts are on their way.')
        onOpenChange(false)
        return
      }

      // Only what actually changed. See the note at the top of this file — this
      // is the difference between fixing a typo and wiping every translation.
      const changes: Partial<MessageInput> = {}
      if (values.module !== detail.module) changes.module = values.module
      if (values.platform !== detail.platform) changes.platform = values.platform
      if (values.baseline_content !== originalBaseline) {
        changes.baseline_content = values.baseline_content
      }
      if (values.context_desc !== (detail.context_desc ?? '')) {
        changes.context_desc = values.context_desc
      }

      if (Object.keys(changes).length > 0) {
        await updateMessage.mutateAsync({ id: detail.id, changes })
      }

      // Saved after the baseline, so an edit the operator typed here wins over
      // the machine draft the cascade just regenerated.
      const edited = Object.entries(drafts).filter(([, draft]) => draft.content !== draft.original)
      for (const [code, draft] of edited) {
        await saveTranslation.mutateAsync({
          message_key: detail.message_key,
          language_code: code,
          content: draft.content,
        })
      }

      toast.success(
        edited.length
          ? `Saved. ${edited.length} translation${edited.length === 1 ? '' : 's'} marked as human-reviewed.`
          : 'Saved.',
      )
      onOpenChange(false)
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Could not save.'
      toast.error(message)
    } finally {
      setSaving(false)
    }
  }

  /**
   * Regenerates one language's machine draft, in place.
   *
   * The dialog stays open and the other rows are untouched — this button sits
   * inside one language's row, so anything wider than that row is a surprise,
   * and closing would throw away every edit typed into the dialog so far.
   *
   * The backend queues the model call and returns immediately, so the draft is
   * collected by polling rather than read from the response.
   */
  async function regenerate(code: string, replaceManual = false) {
    if (!detail) return
    setRegenerating((current) => ({ ...current, [code]: true }))
    try {
      const result = await retranslate.mutateAsync({
        id: detail.id,
        lang: code,
        force: replaceManual,
      })
      if (result.queued === 0) {
        // Only reachable if the row became manual between this dialog opening
        // and the click, on the unforced path.
        toast.info(`${drafts[code].display_name} is human-written, so it was left alone.`)
        return
      }

      for (const delay of POLL_DELAYS_MS) {
        await sleep(delay)
        if (!alive.current) return
        const fresh = await refetchDetail?.()
        const row = fresh?.translations.find((entry) => entry.language_code === code)
        if (!alive.current) return
        if (row?.content) {
          setDrafts((current) => ({
            ...current,
            [code]: {
              ...current[code],
              content: row.content ?? '',
              original: row.content ?? '',
              is_manual: row.is_manual,
              needs_review: row.needs_review,
              status: row.status,
            },
          }))
          toast.success(`New ${drafts[code].display_name} draft.`)
          return
        }
      }
      // Said plainly rather than left spinning: the row really is empty now, and
      // an empty row ships the English text.
      toast.warning(
        `The ${drafts[code].display_name} draft has not come back yet. Reopen this key in a moment — until it lands, the key ships English.`,
      )
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'Could not retranslate.')
    } finally {
      if (alive.current) {
        setRegenerating((current) => ({ ...current, [code]: false }))
      }
    }
  }

  /**
   * Saves the key on its own, not with the rest of the form.
   *
   * Separate because the failure modes are separate: a rename is rejected by
   * uniqueness or by the character rules, and folding that into "Save changes"
   * would fail the whole dialog — including translation edits that were fine —
   * on a problem with one field.
   */
  async function saveKey() {
    if (!detail) return
    const next = form.getValues('message_key').trim()
    if (next === detail.message_key) {
      setRenaming(false)
      return
    }
    // Same regex the server now applies. Checked before the confirm so a typo
    // does not get a scary dialog it was never going to reach.
    const parsed = messageSchema.shape.message_key.safeParse(next)
    if (!parsed.success) {
      form.setError('message_key', { message: parsed.error.issues[0].message })
      return
    }
    setRenameConfirm(true)
  }

  async function commitRename() {
    if (!detail) return
    const next = form.getValues('message_key').trim()
    setSavingKey(true)
    try {
      await updateMessage.mutateAsync({ id: detail.id, changes: { message_key: next } })
      await refetchDetail?.()
      setRenaming(false)
      setRenameConfirm(false)
      toast.success(`Renamed to ${next}. Translations moved with it — call sites did not.`)
    } catch (error) {
      // CONFLICT lands here. Uniqueness is case-insensitive, so this is also what
      // a capitals-only collision reports, and the message would otherwise read
      // as nonsense next to a key that looks new.
      const message = error instanceof ApiError ? error.message : 'Could not rename the key.'
      form.setError('message_key', {
        message:
          error instanceof ApiError && error.code === CODE.CONFLICT
            ? `${message} — keys are compared without case, so this collides with an existing key that differs only in capitals.`
            : message,
      })
      setRenameConfirm(false)
    } finally {
      setSavingKey(false)
    }
  }

  async function submitOverride(code: string) {
    if (!detail || !reason.trim()) return
    try {
      await overrideTranslation.mutateAsync({
        message_key: detail.message_key,
        language_code: code,
        content: drafts[code].content,
        reason: reason.trim(),
      })
      setDrafts((current) => ({
        ...current,
        [code]: { ...current[code], original: current[code].content, needs_review: true, is_manual: true },
      }))
      setOverriding(null)
      setReason('')
      toast.success('Override saved and flagged for review.')
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'Could not override.')
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/*
        The body scrolls and the footer stays put. With a dozen languages the
        translation list is taller than the viewport, and a Save button that has
        to be scrolled to is a Save button people miss.
      */}
      {/*
        Wide on purpose. The translation column holds full sentences in scripts
        that do not wrap where English does, and `sm:max-w-lg` has to be
        overridden explicitly or it wins back at every breakpoint above mobile.
      */}
      <DialogContent className="flex max-h-[90vh] w-[96vw] max-w-none flex-col sm:max-w-[1180px]">
        <DialogHeader>
          <DialogTitle>{isNew ? 'New message' : 'Edit message'}</DialogTitle>
          <DialogDescription className="font-mono text-xs">
            {isNew
              ? 'Machine drafts are generated for every enabled language once you save.'
              : detail.message_key}
          </DialogDescription>
        </DialogHeader>

        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="flex min-h-0 flex-1 flex-col gap-4"
        >
          <div className="-mr-2 min-h-0 flex-1 space-y-4 overflow-y-auto pr-2">
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="sm:col-span-1">
              <Label htmlFor="message_key">Message key</Label>
              <div className="flex items-center gap-1.5">
                <Input
                  id="message_key"
                  {...form.register('message_key')}
                  // Read-only until Edit is pressed. Renaming is possible but it
                  // is not safe in general — frontend call sites reference the
                  // string verbatim, dozens of them for some imported keys, and
                  // nothing in this system can see them. The gate is the point.
                  disabled={!isNew && !renaming}
                  spellCheck={false}
                  className="font-mono text-xs"
                  placeholder="module.section.name"
                />
                {!isNew &&
                  (renaming ? (
                    <>
                      <Button
                        type="button"
                        size="sm"
                        disabled={savingKey}
                        onClick={() => void saveKey()}
                      >
                        Save
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={savingKey}
                        onClick={() => {
                          form.setValue('message_key', detail.message_key)
                          form.clearErrors('message_key')
                          setRenaming(false)
                        }}
                      >
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => setRenaming(true)}
                    >
                      <Pencil className="size-3" />
                      Edit
                    </Button>
                  ))}
              </div>
              {form.formState.errors.message_key && (
                <p className="mt-1 text-xs text-destructive">
                  {form.formState.errors.message_key.message}
                </p>
              )}
              {renaming && !form.formState.errors.message_key && (
                <p className="mt-1 text-xs text-muted-foreground">
                  Letters, numbers and <code>_</code>, dot-separated. Capitals are allowed but do
                  not distinguish keys — uniqueness ignores case.
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="module">Module</Label>
              <Select
                value={form.watch('module')}
                onValueChange={(value) => form.setValue('module', value, { shouldDirty: true })}
              >
                <SelectTrigger id="module">
                  <SelectValue placeholder="Pick a module" />
                </SelectTrigger>
                <SelectContent>
                  {/* Populated from the dictionary, never free text — a typo'd
                      module publishes a file no client ever requests. */}
                  {(modules.data ?? []).map((module) => (
                    <SelectItem key={module.code} value={module.code}>
                      {module.display_name}
                      {module.platform_scope !== 'all' && ` · ${module.platform_scope} only`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="platform">Platform</Label>
              <Select
                value={form.watch('platform')}
                onValueChange={(value) =>
                  form.setValue('platform', value as MessageInput['platform'], { shouldDirty: true })
                }
              >
                <SelectTrigger id="platform">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PLATFORMS.map((platform) => (
                    <SelectItem key={platform} value={platform}>
                      {platform}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="rounded-lg border border-border-strong bg-secondary/50 p-3">
            <Label htmlFor="baseline_content">
              English (baseline){' '}
              <span className="font-normal text-muted-foreground">— single source of truth</span>
            </Label>
            <Textarea
              id="baseline_content"
              rows={3}
              {...form.register('baseline_content')}
              aria-invalid={form.formState.errors.baseline_content ? true : undefined}
              className={form.formState.errors.baseline_content ? 'border-destructive bg-card' : 'bg-card'}
            />
            {form.formState.errors.baseline_content && (
              <p className="mt-1 text-xs text-destructive">
                {form.formState.errors.baseline_content.message}
              </p>
            )}
            {detail?.baseline_content === null && (
              <p className="mt-1.5 text-xs text-[#a9760c]">
                This key has never had an English row — it is translated in other languages only.
                Saving any text here fixes that.
              </p>
            )}

            <div className="mt-2.5">
              <Label htmlFor="context_desc">Context for translators and the model</Label>
              <Input
                id="context_desc"
                {...form.register('context_desc')}
                className="bg-card"
                placeholder="e.g. Primary CTA on the homepage hero"
              />
              {!isNew && (
                <p className="mt-1 text-xs text-muted-foreground">
                  Changing this never re-translates on its own — use Retranslate below.
                </p>
              )}
            </div>

            {cascade && (
              <div className="mt-2.5 flex items-start gap-2 rounded-md bg-warning-soft p-2 text-xs text-[#a9760c]">
                <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
                <div>
                  Saving the English will{' '}
                  {[
                    cascade.machine &&
                      `re-translate ${cascade.machine} machine draft${cascade.machine === 1 ? '' : 's'}`,
                    cascade.human &&
                      `flag ${cascade.human} human translation${cascade.human === 1 ? '' : 's'} for review, content unchanged`,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                  .
                  {baseline.trim() === originalBaseline.trim() && baseline !== originalBaseline && (
                    <>
                      {' '}
                      <b>Whitespace is the only difference</b> — that still counts as a change, and
                      it is sometimes deliberate.
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          {isNew ? (
            <div className="flex items-start gap-2 rounded-md bg-primary-soft p-2.5 text-xs text-accent-foreground">
              <Info className="mt-0.5 size-3.5 shrink-0" />
              <div>Translations appear once the key is saved. Reopen it to review or correct them.</div>
            </div>
          ) : (
            <div>
              <div className="mb-1.5 flex items-baseline gap-2">
                <span className="text-[13px] font-bold">Translations</span>
                <span className="text-[11px] text-muted-foreground">
                  Editing one marks it human-reviewed — automated translation will never overwrite
                  it again
                </span>
              </div>

              {required.length > 0 && (
                <div className="mb-2 flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
                  <span>Must appear in every translation:</span>
                  {required.map((placeholder) => (
                    <code
                      key={placeholder}
                      className="rounded bg-secondary px-1.5 py-0.5 font-mono text-[11px] text-foreground"
                    >
                      {placeholder}
                    </code>
                  ))}
                </div>
              )}

              {/*
                A table rather than a stack of cards: the language and status
                columns line up, so "which of these still needs work" is one
                downward glance instead of a read of every row.
              */}
              <div className="overflow-hidden rounded-lg border border-border-strong">
                <div className={`${ROW_GRID} border-b border-border-strong bg-secondary/60 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wide text-muted-foreground`}>
                  <span>Language</span>
                  <span>Status</span>
                  <span>Translation</span>
                  <span className="sr-only">Actions</span>
                </div>

                {Object.entries(drafts).map(([code, draft]) => {
                  const problem = problems[code]
                  const busy = regenerating[code] === true
                  const status = previewStatus(draft, overriding === code)
                  return (
                    <div
                      key={code}
                      className={`${ROW_GRID} items-start border-b border-border px-3 py-2.5 last:border-b-0`}
                    >
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="truncate text-[13px] font-semibold">
                            {englishName(code, draft.display_name)}
                          </span>
                          {draft.is_rtl && <Badge variant="default">RTL</Badge>}
                        </div>
                        {/* The endonym, which is what the consumer picker shows. */}
                        {englishName(code, draft.display_name) !== draft.display_name && (
                          <div className="truncate text-xs text-muted-foreground">
                            {draft.display_name}
                          </div>
                        )}
                        <code className="font-mono text-[10px] text-muted-foreground">{code}</code>
                      </div>

                      <div className="pt-0.5">
                        <StatusBadge status={status} />
                      </div>

                      <div className="min-w-0">
                        <Textarea
                          rows={2}
                          dir={draft.is_rtl ? 'rtl' : 'ltr'}
                          value={draft.content}
                          disabled={busy}
                          onChange={(event) => setContent(code, event.target.value)}
                          aria-label={`${draft.display_name} translation`}
                          aria-invalid={problem ? true : undefined}
                          className={problem ? 'border-destructive' : undefined}
                          placeholder={
                            busy ? 'Regenerating…' : 'Not translated — will ship the English text'
                          }
                        />

                        {draft.override_reason && (
                          <p className="mt-1 text-[11px] text-[#a9760c]">
                            Override on record: {draft.override_reason}
                          </p>
                        )}
                        {status === 'failed' && (
                          <p className="mt-1 text-[11px] text-destructive">
                            Translation failed{draft.failed_at ? ` ${localStamp(draft.failed_at)}` : ''}:{' '}
                            {failureText(draft.failure_reason)}. Retranslate this row, or type a
                            translation.
                          </p>
                        )}

                        {problem && (
                          <div className="mt-1.5 rounded-md bg-destructive-soft p-2 text-[11px] text-destructive">
                            <p>{problem.message}</p>
                            {!problem.blocking && (
                              <p className="mt-1">
                                This row is not part of this save, so it does not stop it. Fix it
                                here or regenerate it.
                              </p>
                            )}
                            {!problem.overridable ? null : overriding === code ? (
                              <div className="mt-1.5 space-y-1.5">
                                <Input
                                  value={reason}
                                  onChange={(event) => setReason(event.target.value)}
                                  placeholder="Why is this correct despite the mismatch?"
                                  className="bg-card text-xs"
                                  aria-label="Override reason"
                                />
                                <div className="flex gap-1.5">
                                  <Button
                                    type="button"
                                    size="sm"
                                    disabled={!reason.trim() || overrideTranslation.isPending}
                                    onClick={() => void submitOverride(code)}
                                  >
                                    Save override
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                      setOverriding(null)
                                      setReason('')
                                    }}
                                  >
                                    Cancel
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <Button
                                type="button"
                                variant="secondary"
                                size="sm"
                                className="mt-1.5"
                                onClick={() => setOverriding(code)}
                              >
                                Override with a reason
                              </Button>
                            )}
                          </div>
                        )}
                      </div>

                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={busy}
                        title={
                          draft.is_manual
                            ? `Replace the human ${englishName(code, draft.display_name)} translation with a fresh machine draft. Other languages are untouched.`
                            : `Regenerate the ${englishName(code, draft.display_name)} draft. Other languages are untouched.`
                        }
                        // A human row confirms first. The automated paths — a
                        // baseline edit, Fill missing — still never touch it;
                        // what changed is that an operator aiming at one row is
                        // no longer overruled by that rule. It used to be
                        // disabled here, which answered the request by doing
                        // nothing and explaining nothing.
                        onClick={() =>
                          draft.is_manual ? setReplacing(code) : void regenerate(code)
                        }
                      >
                        {busy ? (
                          <Loader2 className="size-3 animate-spin" />
                        ) : (
                          <RotateCw className="size-3" />
                        )}
                        Retranslate
                      </Button>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
          </div>

          <DialogFooter className="border-t border-border pt-3">
            {blockedCount > 0 && (
              <span className="mr-auto text-xs font-semibold text-destructive">
                {blockedCount} translation{blockedCount === 1 ? ' has' : 's have'} a placeholder
                error.
              </span>
            )}
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving || blockedCount > 0}>
              {saving ? 'Saving…' : 'Save changes'}
            </Button>
          </DialogFooter>
        </form>

        {/*
          Confirmed rather than saved outright. The database half is safe — the
          rename moves the translations with it, in one transaction — but the
          half that matters is outside this system entirely, and nothing here
          can check it or undo it.
        */}
        <ConfirmAction
          open={renameConfirm}
          onOpenChange={(open) => !open && setRenameConfirm(false)}
          title="Rename this message key?"
          description={
            <>
              <b>Every frontend call site keeps using the old key</b> and will stop finding this
              message — the app renders the raw key or falls back to nothing, and no error is
              logged anywhere. The console cannot see those call sites, so it cannot tell you how
              many there are; some imported keys have 40+.
              <br />
              <br />
              Safe for a key created minutes ago that nothing references yet. For anything live,
              land the frontend change first.
              <br />
              <br />
              Translations move with the key, so nothing is orphaned on this side.
            </>
          }
          confirmLabel="Rename"
          destructive
          onConfirm={commitRename}
        />

        {/*
          Confirmed because it is the one place a machine translation is allowed
          to replace a human one. Automated paths still never do it — this is an
          operator aiming at a single row and saying so.
        */}
        <ConfirmAction
          open={replacing !== null}
          onOpenChange={(open) => !open && setReplacing(null)}
          title={
            replacing
              ? `Replace the human ${englishName(replacing, drafts[replacing]?.display_name ?? replacing)} translation?`
              : 'Replace this translation?'
          }
          description={
            <>
              A person wrote this one, and <b>their text is not recoverable from here</b> once the
              new draft lands — the console keeps no history of it.
              <br />
              <br />
              The replacement is a machine draft, so it stops being protected: the next baseline
              change will refresh it like any other. It is flagged for review, because it replaced
              somebody&apos;s work.
              <br />
              <br />
              Only this language is affected.
            </>
          }
          confirmLabel="Replace"
          destructive
          onConfirm={async () => {
            const code = replacing
            setReplacing(null)
            if (code) await regenerate(code, true)
          }}
        />
      </DialogContent>
    </Dialog>
  )
}
